import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  Grid,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  TableChart as TableChartIcon,
  Storage as StorageIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';

interface DataSet {
  id: number;
  datasetName: string;
  datasetIdentifier: string;
  worksheetName: string;
  startCell: string;
  endCell: string;
  status: string;
  rowCount: number;
  columnCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DocumentData {
  document: {
    id: number;
    appraisalId: number;
    constructionId: number;
    documentIdentifier: string;
    description?: string;
    primaryWorksheetName: string;
    alternativeWorksheetNames: string[];
    status: string;
    createdAt: string;
    updatedAt: string;
    worksheetSavedAt: string;
  };
  construction: {
    id: number;
    name: string;
    fileName: string;
    blobName: string;
    fileSize: number;
    worksheetCount: number;
    status: string;
    createdAt: string;
  };
  appraisal: {
    id: number;
    name: string;
  };
  worksheetInfo: {
    primaryWorksheet: string;
    alternativeWorksheets: string[];
    savedAt: string;
  };
  dataSets: DataSet[];
  summary: {
    totalDataSets: number;
    dataSetStatuses: Record<string, number>;
  };
}

interface DocumentDataSetsPanelProps {
  documentId: number;
  documentData: DocumentData;
  onUpdate: () => void;
}

export const DocumentDataSetsPanel: React.FC<DocumentDataSetsPanelProps> = ({
  documentId,
  documentData,
  onUpdate
}) => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDataSet, setSelectedDataSet] = useState<DataSet | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuDataSetId, setMenuDataSetId] = useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, dataSetId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuDataSetId(dataSetId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuDataSetId(null);
  };


  const handleEditDataSet = (dataSetId: number) => {
    navigate(`/admin/deliverables/doc-id-system/documents/${documentId}/data-set/${dataSetId}/edit`);
    handleMenuClose();
  };

  const handleDeleteDataSet = (dataSet: DataSet) => {
    setSelectedDataSet(dataSet);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDelete = async () => {
    if (!selectedDataSet) return;

    setDeleting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `/api/appraisals/admin/documents/${documentId}/datasets/${selectedDataSet.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setDeleteDialogOpen(false);
        setSelectedDataSet(null);
        onUpdate(); // Refresh the parent data
      } else {
        throw new Error(result.message || 'Failed to delete data set');
      }
    } catch (err) {
      console.error('Error deleting data set:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete data set');
    } finally {
      setDeleting(false);
    }
  };


  const formatCellRange = (startCell: string, endCell: string) => {
    return `${startCell}:${endCell}`;
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Data Sets ({documentData.dataSets.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/admin/deliverables/doc-id-system/documents/${documentId}/data-set/create`)}
          >
            Add Data Set
          </Button>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

          {documentData.dataSets.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Data Sets Defined
              </Typography>
              <Typography color="text.secondary" paragraph>
                Create data sets to extract specific ranges of data from your Excel worksheets.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/deliverables/doc-id-system/documents/${documentId}/data-set/create`)}
              >
                Create Data Set
              </Button>
            </Paper>
          ) : (
            <>

              {/* Data Sets Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Dataset Name</TableCell>
                      <TableCell>Dataset Identifier</TableCell>
                      <TableCell>Worksheet Name</TableCell>
                      <TableCell>Range</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentData.dataSets.map((dataSet) => (
                      <TableRow key={dataSet.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {dataSet.datasetName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dataSet.datasetIdentifier}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TableChartIcon fontSize="small" />
                            {dataSet.worksheetName}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {formatCellRange(dataSet.startCell, dataSet.endCell)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="More actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, dataSet.id)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            if (menuDataSetId) handleEditDataSet(menuDataSetId);
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem 
            onClick={() => {
              const dataSet = documentData.dataSets.find(ds => ds.id === menuDataSetId);
              if (dataSet) handleDeleteDataSet(dataSet);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => !deleting && setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Data Set</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the data set "{selectedDataSet?.datasetIdentifier}"? 
              This action cannot be undone.
            </DialogContentText>
            {selectedDataSet && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Details:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Worksheet: {selectedDataSet.worksheetName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Range: {formatCellRange(selectedDataSet.startCell, selectedDataSet.endCell)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              color="error"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};